import { AlertCircle, CheckCircle2, MinusCircle, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type {
  AmountReason,
  EnrichmentReasons,
  GeographicReason,
  MarketTypeReason,
  SectoralReason,
} from '../types/suggested-opportunities.types';

/**
 * Sprint 5b.1.5 v2 — badge sous-score avec popover explicatif.
 *
 * Cas `null` (sous-score non calculable) : badge gris avec libellé contextuel.
 * Cas calculé : couleur selon seuil (≥ 70 vert / 40-69 orange / < 40 rouge).
 */

type Dimension = 'sectoral' | 'geographic' | 'amount' | 'marketType';
type Reason = SectoralReason | GeographicReason | AmountReason | MarketTypeReason;

interface SubScoreBadgeProps {
  dimension: Dimension;
  score: number | null;
  reason?: Reason;
  /** Détails optionnels pour le popover (CPV matchés, distance, etc.) */
  details?: { effectiveCpvCount?: number; matchingCpvCount?: number };
  /** Sprint 5b.1.6 — signale la modulation par enrichissement IA (icône Sparkles + texte popover) */
  enrichmentReasons?: EnrichmentReasons;
}

const LABELS: Record<Dimension, string> = {
  sectoral: 'Sectoriel',
  geographic: 'Géo',
  amount: 'Montant',
  marketType: 'Type',
};

const REASON_TEXTS: Record<string, string> = {
  // Sectoral
  effective_match: 'CPV de l\'AO en partie couverts par votre périmètre métier',
  no_effective_cpv: 'Profil métier non configuré (NAF + extensions)',
  no_overlap: 'Aucun CPV de l\'AO ne correspond à votre périmètre',
  no_cpv_on_opportunity:
    'L\'AO n\'a pas de code CPV exploitable (format BOAMP ancien). Sélectionné via filtre descripteur, à qualifier manuellement.',
  // Geographic
  in_primary_zone: 'AO dans une de vos zones d\'intervention principales',
  in_secondary_zone: 'AO dans une de vos zones d\'intervention secondaires',
  within_100km: 'AO à moins de 100 km de votre siège',
  within_300km: 'AO à moins de 300 km de votre siège',
  distant: 'AO à plus de 300 km de votre siège',
  no_zones_configured: 'Aucune zone d\'intervention configurée',
  no_coordinates: 'Coordonnées GPS de l\'AO indisponibles',
  // Amount
  within_range: 'Montant AO dans votre fourchette ciblée',
  below_min: 'Montant AO en-dessous de votre minimum ciblé',
  above_max: 'Montant AO au-dessus de votre maximum ciblé',
  no_amount_in_opportunity: 'Aucun montant estimé public sur cet AO',
  no_target_set: 'Fourchette de montants AO non configurée',
  // Market type
  compatible: 'Type de marché compatible avec votre profil dominant',
  incompatible: 'Type de marché différent de votre profil dominant',
  no_market_type: 'Type de marché non précisé sur l\'AO',
  mixed_perimeter:
    'Votre profil métier est mixte (services + travaux + fournitures), pas de signal exploitable',
  // Sprint 5b.1.6 — modulation géographique par modalité
  remote_detected:
    'Prestation distancielle détectée par l\'IA dans le titre/description — pénalité géographique levée',
  hybrid_detected: 'Modalité hybride détectée par l\'IA — score géographique partiel',
  on_site_confirmed:
    'Présence sur site confirmée par l\'IA — pénalité géographique structurelle maintenue',
};

export function SubScoreBadge({
  dimension,
  score,
  reason,
  details,
  enrichmentReasons,
}: SubScoreBadgeProps) {
  const label = LABELS[dimension];
  // Sprint 5b.1.6 — signaler que ce sous-score a été ajusté par l'enrichissement IA.
  const adjustedByEnrichment =
    (dimension === 'geographic' && !!enrichmentReasons?.geographicAdjusted) ||
    (dimension === 'sectoral' && !!enrichmentReasons?.sectoralAdjusted);

  if (score === null) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex">
            <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
              {label} — n/c
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 text-xs">
          <p className="font-medium mb-1">{label} — non configuré</p>
          <p className="text-muted-foreground">
            {reason ? REASON_TEXTS[reason] ?? reason : 'Score non calculable'}
          </p>
          {reason === 'no_target_set' && (
            <p className="mt-2 text-xs">
              <a
                href="/dossier-company"
                className="text-primary underline hover:no-underline"
              >
                Configurer mes fourchettes →
              </a>
            </p>
          )}
        </PopoverContent>
      </Popover>
    );
  }

  const tone =
    score >= 70 ? 'good' : score >= 40 ? 'warn' : 'bad';
  const Icon = tone === 'good' ? CheckCircle2 : tone === 'warn' ? MinusCircle : AlertCircle;
  const className = cn(
    'inline-flex items-center gap-1 text-xs font-normal',
    tone === 'good' && 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
    tone === 'warn' && 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    tone === 'bad' && 'bg-rose-100 text-rose-800 hover:bg-rose-100',
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex">
          <Badge className={className}>
            <Icon className="h-3 w-3" />
            <span>{label}</span>
            <span className="font-mono font-semibold">{score}</span>
            {adjustedByEnrichment && (
              <Sparkles className="h-3 w-3" aria-label="ajusté par IA" />
            )}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs">
        <p className="font-medium mb-1">
          {label} — score {score}/100
          {adjustedByEnrichment && (
            <span className="ml-1 text-muted-foreground">(ajusté par IA)</span>
          )}
        </p>
        <p className="text-muted-foreground">
          {reason ? REASON_TEXTS[reason] ?? reason : 'Détail non disponible'}
        </p>
        {dimension === 'sectoral' && details && details.matchingCpvCount !== undefined && (
          <p className="mt-2">
            <span className="font-mono">{details.matchingCpvCount}</span> CPV de l'AO sur{' '}
            <span className="font-mono">{details.effectiveCpvCount ?? '?'}</span> CPV de votre
            périmètre matchent.
          </p>
        )}
        {dimension === 'sectoral' &&
          enrichmentReasons?.sectoralAdjusted === 'semantic_out_of_perimeter' && (
            <p className="mt-2 text-xs text-amber-700">
              Sujet réel détecté hors de votre périmètre métier. À vérifier dans le DCE.
            </p>
          )}
      </PopoverContent>
    </Popover>
  );
}
